import { setAlpha } from '@talos/kyoko';
import type {
  AccessList,
  AccessListItem,
  ChartingLibraryFeatureset,
  ChartingLibraryWidgetOptions,
  LanguageCode,
  ResolutionString,
  StudyOverrides,
  Timezone,
  WidgetOverrides,
} from '@trading-view/charting_library';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';

export interface UseTradingViewWidgetOptionsParams {
  symbol: string;
  studiesAccess?: AccessList['tools'][number]['name'][];
  hideTradingViewVolume?: boolean;
  interval: ResolutionString;
  locale: string;
}

export const useTradingViewWidgetOptions = ({
  symbol,
  studiesAccess,
  hideTradingViewVolume,
  interval,
  locale,
}: UseTradingViewWidgetOptionsParams) => {
  const theme = useTheme();

  const studiesAccessObj: AccessList | undefined = useMemo(() => {
    if (!studiesAccess) {
      return undefined;
    }
    const studies: AccessListItem[] = studiesAccess.map(study => ({
      name: study,
      grayed: false,
    }));

    return {
      tools: studies,
      type: 'white',
    };
  }, [studiesAccess]);

  return useMemo(() => {
    const {
      name,
      baseSize,
      backgroundChart,
      backgroundChartUpColor,
      backgroundChartDownColor,
      borderColorChartUpColor,
      borderColorChartDownColor,
      borderColorChartCrosshair,
      borderColorChartGridLine,
      colorTextDefault,
      fontSizeSmall,
      colors,
    } = theme;

    const overrides: Partial<WidgetOverrides> = {
      'paneProperties.background': backgroundChart,
      'paneProperties.backgroundType': 'solid',
      'paneProperties.vertGridProperties.color': borderColorChartGridLine,
      // cSpell:disable-next-line
      'paneProperties.horzGridProperties.color': borderColorChartGridLine,
      'paneProperties.crossHairProperties.color': borderColorChartCrosshair,
      'paneProperties.crossHairProperties.width': 1,

      'scalesProperties.backgroundColor': backgroundChart,
      'scalesProperties.fontSize': baseSize * fontSizeSmall,
      'scalesProperties.lineColor': borderColorChartGridLine,
      'scalesProperties.textColor': colorTextDefault,
      'scalesProperties.axisHighlightColor': setAlpha(0.25, colors.blue.lighten),
      'scalesProperties.showSeriesLastValue': false,

      'scalesProperties.axisLineToolLabelBackgroundColorActive': colors.blue.lighten,
      'scalesProperties.axisLineToolLabelBackgroundColorCommon': colors.blue.lighten,

      'paneProperties.legendProperties.showStudyArguments': false,
      'paneProperties.legendProperties.showStudyTitles': true,
      'paneProperties.legendProperties.showSeriesTitle': false,

      'mainSeriesProperties.candleStyle.upColor': backgroundChartUpColor,
      'mainSeriesProperties.candleStyle.downColor': backgroundChartDownColor,
      'mainSeriesProperties.candleStyle.borderColor': borderColorChartUpColor,
      'mainSeriesProperties.candleStyle.borderUpColor': borderColorChartUpColor,
      'mainSeriesProperties.candleStyle.borderDownColor': borderColorChartDownColor,
      'mainSeriesProperties.candleStyle.wickUpColor': borderColorChartUpColor,
      'mainSeriesProperties.candleStyle.wickDownColor': borderColorChartDownColor,

      'mainSeriesProperties.barStyle.upColor': backgroundChartUpColor,
      'mainSeriesProperties.barStyle.downColor': backgroundChartDownColor,
    };

    const studiesOverrides: Partial<StudyOverrides> = {
      'volume.volume.color.0': borderColorChartDownColor,
      'volume.volume.color.1': borderColorChartUpColor,
    };

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone as Timezone;

    const disabledFeatures: ChartingLibraryFeatureset[] = [
      'header_symbol_search',
      'symbol_search_hot_key',
      'save_shortcut',
      'header_chart_type',
      'header_settings',
      'header_compare',
      'header_undo_redo',
      'header_screenshot',
      'header_fullscreen_button',
      'context_menus',
      'legend_context_menu',
      'pane_context_menu',
      'control_bar',
      'timeframes_toolbar',
      'main_series_scale_menu',
      'edit_buttons_in_legend',
      'display_market_status',
    ];

    if (hideTradingViewVolume) {
      disabledFeatures.push('create_volume_indicator_by_default');
      disabledFeatures.push('volume_force_overlay');
    }

    if (!studiesAccessObj) {
      disabledFeatures.push('header_indicators');
    }

    const widgetOptions: Omit<ChartingLibraryWidgetOptions, 'container' | 'datafeed'> = {
      debug: false,
      library_path: `/static/tradingview/charting_library/`,
      symbol,
      interval,
      locale: locale as LanguageCode,
      autosize: true,
      theme: name.includes('Dark') ? 'dark' : 'light',
      custom_css_url: `/assets/tradingview/${name}.css`,
      custom_font_family: theme.fontFamily,
      studies_access: studiesAccessObj,
      disabled_features: disabledFeatures,
      timezone,
      overrides,
      studies_overrides: studiesOverrides,
      auto_save_delay: 1,
    };

    return {
      widgetOptions,
      overrides,
    };
  }, [theme, symbol, studiesAccessObj, hideTradingViewVolume, interval, locale]);
};
